/* eslint-disable max-len */
import {v4 as uuidv4} from 'uuid';
import {Modal, notification} from 'antd';
import {io, Socket} from 'socket.io-client';
import Router from 'next/router';
import {getGameEventV3} from '@constant/game';
import {getFailRedirectUrl} from '@utils/index';

interface SubscribeParams {
  event: string;
  parameters?: object;
}

interface SocketClient {
  path: string;
  socket: Socket;
  [x: string]: any;
}

interface Options {
  withNotification?: boolean;
}

class SocketClient implements SocketClient {
  path: string;

  socket: Socket;

  on: Function;

  off: Function;

  constructor(path: string, token: string) {
    const BROWSER_TAB_ID_KEY = 'browserTabId';
    let tabId: string = null;
    if (process.browser) {
      tabId = sessionStorage.getItem(BROWSER_TAB_ID_KEY);
      if (!tabId) {
        const id = uuidv4();
        sessionStorage.setItem(BROWSER_TAB_ID_KEY, id);
        tabId = id;
      }
    }

    this.path = path;
    this.socket = io(path, {
      forceNew: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 8000,
      reconnectionAttempts: 10000000,
      transports: ['websocket'],
      withCredentials: true,
      auth: {token, tabId},
    });
    this.on = this.socket.on;
    this.off = this.socket.off;
  }

  waitUntilConnected = async () => {
    if (this.socket?.connected) {
      return Promise.resolve();
    }
    return new Promise(res => this.socket.once('connect', res));
  };

  call = async (event, params?: object, options?: Options) => {
    await this.waitUntilConnected();
    return new Promise((res, rej) => {
      this.socket.emit(event, params || {}, response => {
        // TODO: use response.data or response.error
        if (response.status === 200 || response.success) {
          res(response);
        } else {
          if (process.env.NODE_ENV === 'production') {
            console.log(' *** error in socket.call *** ', response);
          } else if (options?.withNotification || typeof options === 'undefined') {
            this.showError(response?.message);
          }
          rej(response);
        }
      });
    });
  };

  disconnect = async () => {
    this.socket?.disconnect();
  };

  connect = async () => {
    this.socket?.connect();
  };

  subscribe = async (event, parameters, callBack) => {
    const params: SubscribeParams = {event};
    if (parameters) {
      params.parameters = parameters;
    }
    const response = await this.call('/v1/channel/subscribe', params);
    if (response && callBack) {
      callBack(response);
    }
    this.socket.on(event, callBack);
    return async () => {
      this.socket.off(event, callBack);
      const res = await this.call('/v1/channel/unsubscribe', {event});
      return res;
    };
  };

  testRes = async (callBack: Function) => {
    this.socket.on('testRes', data => callBack(data));
  };

  showError = (message: string) => {
    notification.error({
      style: {width: '100%'},
      message,
    });
  };
}

export default SocketClient;
